import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { ManageOrganisationService } from '@/services/manageOrganisation.services';
import axiosInstance from '@/services/interceptor.services';
import type {
  IManageOrganisation,
  IOrganisationStore,
} from '@/types/manageOrgCommerce.types';
import type { IRestaurant } from '@/types/commerce.types';

function extractOrgs(response: any): IManageOrganisation[] {
  const d = response?.data;
  return Array.isArray(d) ? d : (d?.data ?? []);
}

function extractTotal(response: any): number {
  // total = nombre total dans la BDD (pagination)
  const t = response?.data?.total;
  return typeof t === 'number' ? t : 0;
}

// Champs manquants calculables depuis la liste (sans détail)
// On utilise uniquement ce que /organizations/search retourne :
//   id, name, isActive, storeCount, logoUrl, logoId
// contacts / addresses / stores ne sont PAS dans la liste → on les ignore ici
function missingCount(org: IManageOrganisation): number {
  let n = 0;
  if (!org.logoUrl && !org.logoId) n++; // logo manquant
  if ((org.storeCount ?? 0) === 0) n++; // aucun commerce configuré
  // contacts/addresses : non disponibles en liste → on compte 1 si org inactive
  if (!org.isActive) n++;
  return n;
}

export function useDashboardStats() {
  const {
    data: rawData,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['GetOrganisations'],
    queryFn: async () => {
      // Page 1 pour connaître total et pageSize
      const first = await ManageOrganisationService.searchOrganisations(
        1,
        [],
        '',
        '',
      );
      const meta = first.data as any;
      const total: number = meta?.total ?? 0;
      const pageSize: number = meta?.pageSize ?? 20;
      const firstOrgs = extractOrgs(first);

      let allOrgs: IManageOrganisation[];
      if (total <= pageSize) {
        allOrgs = firstOrgs;
      } else {
        const totalPages = Math.ceil(total / pageSize);
        const pages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(
          pages.map((p) =>
            ManageOrganisationService.searchOrganisations(p, [], '', '').then(
              extractOrgs,
            ),
          ),
        );
        allOrgs = [...firstOrgs, ...rest.flat()];
      }

      // Charger les stores pour chaque org active (restaurants/search?organizationId=)
      const activeOrgsToFetch = allOrgs.filter((o) => o.isActive);
      const storeResults = await Promise.all(
        activeOrgsToFetch.map((o) =>
          axiosInstance
            .get<{ data: IRestaurant[] }>(
              `restaurants/search?pagination=true&page=1&pageSize=50&organizationId=${o.id}`,
            )
            .then((r) => ({
              orgId: o.id,
              stores: (r.data.data ?? []) as IRestaurant[],
            }))
            .catch(() => ({ orgId: o.id, stores: [] as IRestaurant[] })),
        ),
      );

      // Mapper les stores vers IOrganisationStore et les attacher aux orgs
      const storeMap = new Map(storeResults.map((r) => [r.orgId, r.stores]));
      const orgsWithStores = allOrgs.map((o) => ({
        ...o,
        stores: (storeMap.get(o.id) ?? []).map((s) => ({
          id: s.id,
          designation: s.designation,
          slug: s.slugs?.[0]?.slug ?? null,
          isActive: s.isActive ?? true,
        })) as IOrganisationStore[],
      }));

      return { orgs: orgsWithStores, total };
    },
    staleTime: 60_000,
  });

  const orgs = computed<IManageOrganisation[]>(() => rawData.value?.orgs ?? []);
  const total = computed<number>(() => {
    const t = rawData.value?.total ?? 0;
    // fallback sur la taille du tableau si le total API est 0 mais on a des orgs
    return t > 0 ? t : orgs.value.length;
  });

  // ── KPIs ──────────────────────────────────────────────────────────────────

  // Sociétés = total retourné par l'API (toutes pages confondues)
  const totalSocietes = total;

  // Commerces = somme de storeCount (disponible dans la liste)
  const totalCommerces = computed(() =>
    orgs.value.reduce((acc, o) => acc + (o.storeCount ?? 0), 0),
  );

  // Orgs actives / inactives
  const activeOrgs = computed(() => orgs.value.filter((o) => o.isActive));
  const inactiveOrgs = computed(() => orgs.value.filter((o) => !o.isActive));

  // Alertes prioritaires = orgs ACTIVES sans aucun commerce configuré
  // (client actif mais aucun point de vente → besoin d'intervention)
  const alertCount = computed(
    () => activeOrgs.value.filter((o) => (o.storeCount ?? 0) === 0).length,
  );

  // Tâches en attente = orgs inactives (chaque org inactive = profil à finaliser)
  // + orgs actives sans logo (champ manquant visible dans la liste)
  const taskCount = computed(() => {
    const inactive = inactiveOrgs.value.length;
    const activeMissingLogo = activeOrgs.value.filter(
      (o) => !o.logoUrl && !o.logoId,
    ).length;
    return inactive + activeMissingLogo;
  });

  // Santé globale = % d'orgs actives avec au moins 1 commerce configuré
  const healthPct = computed(() => {
    if (!activeOrgs.value.length) return 0;
    const healthy = activeOrgs.value.filter(
      (o) => (o.storeCount ?? 0) > 0,
    ).length;
    return Math.round((healthy / activeOrgs.value.length) * 100);
  });
  const healthLabel = computed(() => {
    const p = healthPct.value;
    if (p >= 80) return 'Bonne';
    if (p >= 50) return 'Moyenne';
    return 'Faible';
  });

  // Commerces actifs (stores.isActive) — non disponible en liste, on utilise storeCount comme proxy
  // Si stores n'est pas chargé, on suppose que storeCount > 0 = au moins 1 actif
  const totalCommerceActifs = computed(() =>
    orgs.value.reduce((acc, o) => {
      if (o.stores && o.stores.length > 0)
        return acc + o.stores.filter((s) => s.isActive).length;
      // fallback : si storeCount > 0 et org active, on compte storeCount
      return acc + (o.isActive ? (o.storeCount ?? 0) : 0);
    }, 0),
  );

  // Orgs prioritaires à compléter (pour DashOnboarding)
  // Triées : inactives d'abord, puis par storeCount croissant (moins configurées)
  const incompleteOrgs = computed(() =>
    [...orgs.value]
      .filter(
        (o) =>
          !o.isActive || (o.storeCount ?? 0) === 0 || (!o.logoUrl && !o.logoId),
      )
      .sort((a, b) => {
        // inactives avant actives
        if (!a.isActive && b.isActive) return -1;
        if (a.isActive && !b.isActive) return 1;
        // puis par storeCount croissant (moins de commerces = plus urgent)
        return (a.storeCount ?? 0) - (b.storeCount ?? 0);
      })
      .slice(0, 3),
  );

  return {
    orgs,
    totalSocietes,
    totalCommerces,
    totalCommerceActifs,
    activeOrgs,
    inactiveOrgs,
    healthPct,
    healthLabel,
    alertCount,
    taskCount,
    incompleteOrgs,
    isPending,
    isError,
  };
}
